import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Modal, Button, Space, Spin, Select } from "antd";
import printJS from "print-js";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale } from "../../utils";
import {
  getDesktopPrintTemplate,
  listTemplates,
} from "./print-templates/storage";
import { flattenRowChildren } from "./print-templates/catalog";
import TemplateCanvas from "./print-templates/components/TemplateCanvas";
import alpEkranFont from "../../styles/fonts/AlpEkran.ttf";
import "./OrderPrintView.scss";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();

function buildPrintTemplateCss(isUg) {
  const ugFontFace = isUg
    ? `@font-face { font-family: "Alp Ekran"; src: url("${alpEkranFont}") format("truetype"); font-weight: normal; font-style: normal; }`
    : "";
  const bodyFont = isUg
    ? `"Alp Ekran", sans-serif`
    : `-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif`;
  const tableAlign = isUg ? "start" : "left";
  return `
  ${ugFontFace}
  * { box-sizing: border-box; }
  body { margin: 0; font-family: ${bodyFont}; color: #222; }
  .template-canvas { display: flex; justify-content: center; padding: 0; }
  .template-canvas__page { background: #fff; padding: 16px 20px; box-sizing: border-box; width: 100%;${
    isUg ? ' direction: rtl; font-family: "Alp Ekran", sans-serif;' : ""
  } }
  .template-canvas.paper-A4 .template-canvas__page { max-width: 794px; }
  .template-canvas.paper-80mm .template-canvas__page,
  .template-canvas.paper-58mm .template-canvas__page { max-width: 302px; padding: 8px 6px; }
  .template-canvas__row { display: grid; grid-template-columns: repeat(12, 1fr); gap: 4px 8px; margin-bottom: 4px; }
  .tpl-comp { min-height: 18px; padding: 1px 2px; }
  .tpl-comp__image-box { border: 1px dashed #d9d9d9; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .tpl-comp__image-box img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .tpl-comp__qr-img { display: flex; justify-content: center; }
  .tpl-comp__qr-img img { display: block; }
  .tpl-comp__stack-label,
  .tpl-comp__drag-handle,
  .tpl-comp__eject,
  .tpl-comp__resize,
  .tpl-comp__span-badge,
  .template-canvas__col-guides { display: none !important; }
  .tpl-comp--stack {
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
  }
  .tpl-comp__barcode { letter-spacing: -1px; overflow: hidden; font-size: 18px; line-height: 1; color: #222; }
  .tpl-comp__barcode-img { max-width: 100%; height: auto; display: block; margin: 0 auto; image-rendering: crisp-edges; }
  .tpl-comp__barcode-svg { max-width: 100%; height: auto !important; display: block; margin: 0 auto; }
  .tpl-comp__barcode-qr { display: flex; justify-content: center; }
  .tpl-comp__barcode-qr svg { display: block; }
  .tpl-comp__barcode-val { font-size: 10px; margin-top: 2px; text-align: center; }
  .tpl-comp__barcode-fallback { font-size: 12px; word-break: break-all; text-align: center; }
  .tpl-comp__qr { border: 1px solid #d9d9d9; display: flex; align-items: center; justify-content: center; margin: 0 auto; }
  .tpl-comp--table table { width: 100%; border-collapse: collapse; }
  .tpl-comp--table th, .tpl-comp--table td { padding: 3px 4px; text-align: ${tableAlign}; border-bottom: 1px solid #f0f0f0; word-break: break-word; }
  .tpl-comp--table table.no-border th, .tpl-comp--table table.no-border td { border: none; }
  .tpl-comp--table th { border-bottom: 1px solid #d9d9d9; }
  .tpl-comp__summary td { border-top: 1px solid #d9d9d9 !important; }
  .tpl-comp--stack { display: flex; flex-direction: column; gap: 2px; align-self: start; }
`;
}

function desktopTemplateOptions(merchantId) {
  const list = listTemplates(merchantId);
  const enabled = list.filter((t) => t.desktopEnabled !== false);
  return enabled.length ? enabled : list;
}

function OrderPrintView({ user, order, onCancel }) {
  const merchantId = user?.merchant?.id || order?.merchant?.id;
  const templates = useMemo(
    () => desktopTemplateOptions(merchantId),
    [merchantId]
  );
  const defaultTemplate = useMemo(
    () => getDesktopPrintTemplate(merchantId),
    [merchantId]
  );
  const [templateId, setTemplateId] = useState(
    () => (defaultTemplate && String(defaultTemplate.id)) || ""
  );
  const template = useMemo(() => {
    const selected = templates.find((t) => String(t.id) === String(templateId));
    return selected || defaultTemplate || templates[0] || null;
  }, [templates, templateId, defaultTemplate]);

  const [printOrder, setPrintOrder] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);

  useEffect(() => {
    if (!template) return;
    if (!templates.some((t) => String(t.id) === String(templateId))) {
      setTemplateId(String(template.id));
    }
  }, [templates, template, templateId]);

  useEffect(() => {
    let cancelled = false;
    if (!order) {
      setPrintOrder(null);
      return undefined;
    }

    const base = {
      ...order,
      merchant: {
        ...(user?.merchant || {}),
        ...(order.merchant || {}),
      },
    };

    const needsMiniQr = Boolean(
      (template?.layout?.rows || []).some((row) =>
        flattenRowChildren(row.children || []).some(
          (c) => c.type === "qrcode" && c.field === "order_url"
        )
      )
    );

    async function load() {
      if (!needsMiniQr || !order.id || !user?.token) {
        if (!cancelled) setPrintOrder(base);
        return;
      }
      setLoadingQr(true);
      try {
        const res = await api.orderv6.publicQrcode.run({
          token: user.token,
          path: { orderId: order.id },
        });
        if (cancelled) return;
        if (res?.result && res.data?.qrcode_url) {
          setPrintOrder({
            ...base,
            public_qrcode_url: res.data.qrcode_url,
            order_url: res.data.qrcode_url,
          });
        } else {
          setPrintOrder(base);
        }
      } catch {
        if (!cancelled) setPrintOrder(base);
      } finally {
        if (!cancelled) setLoadingQr(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [order, user?.merchant, user?.token, template]);

  const modalWidth =
    template?.paperSize === "80mm" || template?.paperSize === "58mm"
      ? 420
      : 860;

  const printNow = () => {
    printJS({
      printable: "printView",
      type: "html",
      scanStyles: false,
      style: buildPrintTemplateCss(template?.lang === "ug"),
      documentTitle: template?.name || i18n.t("order.print"),
    });
  };

  if (!order) return null;

  return (
    <Modal
      width={modalWidth}
      className="route-order-list-print-modal order-print-template-modal"
      open
      title={i18n.t("order.print")}
      onCancel={onCancel}
      footer={
        <Space>
          <Button onClick={onCancel}>{i18n.t("base.modal.cancelText")}</Button>
          <Button type="primary" onClick={printNow} disabled={!printOrder || loadingQr}>
            {i18n.t("order.print")}
          </Button>
        </Space>
      }
    >
      {templates.length > 0 ? (
        <div className="order-print-template-modal__toolbar">
          <span className="order-print-template-modal__toolbar-label">
            {i18n.t("order.printTemplate.chooseTemplate")}
          </span>
          <Select
            className="order-print-template-modal__template-select"
            value={template ? String(template.id) : undefined}
            onChange={(id) => setTemplateId(String(id))}
            options={templates.map((t) => ({
              value: String(t.id),
              label: `${t.name}${
                t.paperSize ? ` · ${t.paperSize}` : ""
              }${t.lang === "ug" ? ` · ${i18n.t("order.printTemplate.langUg")}` : ""}`,
            }))}
          />
        </div>
      ) : null}
      <Spin spinning={loadingQr}>
        <div id="printView" className="order-print-template-modal__sheet">
          {template && printOrder ? (
            <TemplateCanvas
              template={template}
              order={printOrder}
              zoom={1}
              editable={false}
            />
          ) : (
            <div className="order-print-template-modal__empty">
              {template
                ? null
                : i18n.t("order.printTemplate.notFound")}
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(OrderPrintView);
